import { useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/base/dialog';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';

import { novaChave } from './dados';

export interface DialogoDeAcaoProps {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao: ReactNode;
  rotuloDoBotao: string;
  /** Ação destrutiva (bloquear, apagar segundo fator) usa o botão de perigo. */
  perigosa?: boolean;
  /** Pede o motivo, que vai para a trilha com quem fez. */
  pedeMotivo?: boolean;
  /** Aviso que não impede a ação (o e-mail vai para a caixa de testes). */
  aviso?: string | null;
  /** Motivo pelo qual a ação não pode ser feita agora; o botão fica desligado. */
  bloqueio?: string | null;
  /** Executa a ação. Rejeita com ErroApi para a mensagem aparecer no diálogo. */
  aoConfirmar: (dados: { motivo: string; chaveIdempotencia: string }) => Promise<void>;
}

/**
 * Diálogo das ações de conta e de convite. Nada acontece na tela antes da
 * resposta da API: o diálogo fica aberto com "Aguarde" e só fecha quando ela
 * confirma. Se falhar, a mensagem da API aparece aqui dentro.
 *
 * Chave de idempotência: a mesma enquanto a pessoa repete o MESMO pedido
 * (clicar de novo depois de uma falha de rede); nova assim que o conteúdo muda
 * (outro motivo). A API recusa a mesma chave com outro pedido.
 */
export function DialogoDeAcao({
  aberto,
  aoFechar,
  titulo,
  descricao,
  rotuloDoBotao,
  perigosa = false,
  pedeMotivo = true,
  aviso,
  bloqueio,
  aoConfirmar,
}: DialogoDeAcaoProps) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const chave = useRef(novaChave());

  const motivoValido = !pedeMotivo || motivo.trim().length >= 5;

  function fechar() {
    if (enviando) return;
    setMotivo('');
    setErro(null);
    chave.current = novaChave();
    aoFechar();
  }

  async function confirmar() {
    if (!motivoValido || bloqueio) return;
    setEnviando(true);
    setErro(null);
    try {
      await aoConfirmar({ motivo: motivo.trim(), chaveIdempotencia: chave.current });
      setMotivo('');
      chave.current = novaChave();
      setEnviando(false);
      aoFechar();
    } catch (falha) {
      setEnviando(false);
      setErro(falha instanceof ErroApi ? falha.aviso : 'Não foi possível concluir. Tente de novo.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && fechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{descricao}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {(bloqueio || aviso) && (
            <Alert>
              <Info className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{bloqueio ?? aviso}</AlertDescription>
            </Alert>
          )}
          {pedeMotivo && !bloqueio && (
            <div className="space-y-2">
              <Label htmlFor="acao-motivo">Motivo</Label>
              <Textarea
                id="acao-motivo"
                value={motivo}
                maxLength={500}
                rows={3}
                aria-describedby="acao-motivo-ajuda"
                onChange={(e) => {
                  chave.current = novaChave();
                  setMotivo(e.target.value);
                }}
              />
              <p id="acao-motivo-ajuda" className="text-xs text-muted-foreground">
                Fica no histórico administrativo, com o seu nome. Pelo menos 5 caracteres.
              </p>
            </div>
          )}
          {erro && (
            <Alert variant="destructive">
              <AlertDescription>{erro}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={fechar} disabled={enviando}>
            Voltar
          </Button>
          {!bloqueio && (
            <Button
              type="button"
              variant={perigosa ? 'destructive' : 'default'}
              disabled={enviando || !motivoValido}
              onClick={() => void confirmar()}
            >
              {enviando ? 'Aguarde…' : rotuloDoBotao}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
